import { useEffect, useState } from 'react';
import { Loader2, Play, Upload } from 'lucide-react';
import { apiFetch, getApiBase } from './apiBase';
import { maskUsDateInput, parseEffectiveDateInput } from './effectiveDateInput';
import ElementEntriesTable from './ElementEntriesTable';

export default function ElementEntriesReport({ onOracleReady, connectionRefresh }) {
  const [status, setStatus] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const parsed = parseEffectiveDateInput(effectiveDate);
  const dateReady = parsed.ok;

  const loadStatus = () => {
    apiFetch('/api/reports/element-entries/status')
      .then(setStatus)
      .catch((e) => setError(e.message));
  };

  useEffect(() => {
    loadStatus();
  }, [connectionRefresh]);

  useEffect(() => {
    if (status?.rowCount > 0) {
      setTableRefreshKey((k) => k + 1);
    }
  }, [status?.rowCount]);

  const uploadReport = async (file) => {
    if (!file) return;
    setError('');
    setResult(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const base = getApiBase();
      const res = await fetch(`${base}/api/reports/element-entries/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setResult(data);
      setTableRefreshKey((k) => k + 1);
      if (onOracleReady && data.reconReady) {
        onOracleReady({ source: 'element-entries-upload', ...data });
      }
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const runReport = async () => {
    setError('');
    setResult(null);
    const check = parseEffectiveDateInput(effectiveDate);
    if (!check.ok) {
      setError(check.error);
      return;
    }
    setRunning(true);
    try {
      const data = await apiFetch('/api/reports/element-entries/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          effectiveDate: check.iso,
          format: 'xlsx',
          saveToDocs: true,
        }),
      });
      if (!data.ok) throw new Error(data.error || 'Report failed');
      setResult(data);
      setTableRefreshKey((k) => k + 1);
      if (onOracleReady && data.reconReady) {
        onOracleReady({ source: 'element-entries-report', ...data });
      }
    } catch (e) {
      const msg = e.message || 'Report failed';
      if (/startDate is required/i.test(msg)) {
        setError('Enter the effective date as MM/DD/YYYY (for example 03/19/2026), then try again.');
      } else if (/SOAP request timed out|did not finish within/i.test(msg)) {
        setError(
          'The report took too long to finish. Try again, or ask your consultant if Oracle reports are running slowly.'
        );
      } else {
        setError(msg);
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-slate-300">Effective date</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="MM/DD/YYYY"
            className="input-field w-36 font-mono"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(maskUsDateInput(e.target.value))}
          />
        </label>
        <button
          type="button"
          onClick={runReport}
          disabled={running || uploading || !dateReady || !status?.connectionConfigured}
          className="btn-primary"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {running ? 'Running…' : 'Run report'}
        </button>
        <label className="btn-secondary inline-flex cursor-pointer">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Upload Excel
          <input
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            disabled={uploading || running}
            onChange={(e) => {
              const f = e.target.files?.[0];
              uploadReport(f);
              e.target.value = '';
            }}
          />
        </label>
        {status?.connection?.name && (
          <span className="text-xs text-slate-500">{status.connection.name}</span>
        )}
      </div>

      {error && <p className="alert-error mb-3">{error}</p>}

      {result?.ok && (
        <div className="alert-success mb-3 text-sm">
          {result.rowCount?.toLocaleString()} rows as of {result.effectiveDate}
          {result.savedPath ? ' · saved' : ''}
        </div>
      )}

      <ElementEntriesTable refreshKey={tableRefreshKey} />
    </div>
  );
}
